import type { ReactNode } from 'react';

import { Button, Typography } from '../ui';
import styles from './EmptyIntro.module.css';

/**
 * The shared "wake-up" empty state — Figma `5773:9154`, instanced by both frames on the
 * Empty States page (`10941:74616`): an illustration, the display-size title, three
 * numbered steps, and the black CTA that hands off to the Maestro.
 *
 * The same block ships on native as `HomeEmptyState` / `AgentsEmptyIntro`
 * (`android/.../features/Pages.kt`, `ios/.../SymphonyHomeView.swift`) — the steps run in a
 * row here because the desktop frame has the width for it, and stack below 720px like
 * native does.
 */
export function EmptyIntro({
  illustration,
  title,
  steps,
  ctaLabel,
  onCta,
}: {
  illustration: ReactNode;
  title: string;
  /** Exactly the three steps of the design; numbered 1..3 in order. */
  steps: [string, string, string];
  ctaLabel: string;
  onCta: () => void;
}) {
  return (
    <div className={styles.root}>
      <div className={styles.illustration}>{illustration}</div>
      <div className={styles.copy}>
        <Typography variant="h1" component="h1" color="strong" className={styles.title}>
          {title}
        </Typography>
        <ol className={styles.steps}>
          {steps.map((step, i) => (
            <li key={step} className={styles.step}>
              {/* The <ol> already conveys the order, so the numeral is decorative;
                  `color="inherit"` leaves the light-on-black to the badge itself. */}
              <Typography variant="p3" component="span" color="inherit" className={styles.badge} aria-hidden>
                {i + 1}
              </Typography>
              <Typography variant="p2" component="span" color="strong" className={styles.stepText}>
                {step}
              </Typography>
            </li>
          ))}
        </ol>
        <Button onClick={onCta}>{ctaLabel}</Button>
      </div>
    </div>
  );
}
